import { useState } from "react";
import {
  CopyOutlined,
  DownloadOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import { Dropdown, message as antdMessage } from "antd";

import { trackNeoEvent } from "../../../pages/Neo/neoAnalytics";
import {
  containsHeredoc,
  countWords,
  downloadJsonText,
  downloadNeoDocument,
  extractMessageJson,
  formatLabel,
  hasMarkdownTable,
  messagePdfContentParts,
  messageText,
  type NeoDocumentFormat,
} from "./messageExport";
import type { CopilotMessage } from "./types";
import styles from "./styles.module.scss";

/** Below this, a non-final reply ("Let me pull that up…") gets no actions. */
const MESSAGE_ACTIONS_MIN_WORDS = 100;

/** Copy / Regenerate / Download under an assistant reply — same rules as the
 *  old Neo page's message action row. */
const MessageActions = ({
  message,
  isFinal,
  streaming,
  onRegenerate,
}: {
  message: CopilotMessage;
  /** Last assistant reply in the conversation. */
  isFinal: boolean;
  /** Conversation is streaming a turn right now. */
  streaming: boolean;
  onRegenerate?: () => void;
}) => {
  const [downloading, setDownloading] = useState(false);
  const text = messageText(message);
  const contentParts = messagePdfContentParts(message);

  // A partial reply would export half a document; the final reply streams
  // last, so hiding it while the turn is live covers that.
  if (isFinal && streaming) return null;
  if (!text && !contentParts) return null;
  if (!isFinal && !contentParts && countWords(text) < MESSAGE_ACTIONS_MIN_WORDS)
    return null;

  const hasTabular =
    Boolean(contentParts?.some((part) => part.type === "neo_chart")) ||
    hasMarkdownTable(text);
  const json =
    !contentParts && !hasTabular && text ? extractMessageJson(text) : null;
  // Heredoc documents carry their own download card; a message-level one would
  // post the raw delimiters. Charts live outside heredocs, so keep it for them.
  const showDownload = !containsHeredoc(message) || Boolean(contentParts);

  const copy = () => {
    if (!navigator.clipboard?.writeText) {
      antdMessage.error("Copy is not available in this browser.");
      return;
    }
    navigator.clipboard
      .writeText(text)
      .then(() => {
        antdMessage.success("Copied response.");
        trackNeoEvent("response copied", {
          message_id: message.id,
          text_length: text.length,
        });
      })
      .catch(() => {
        antdMessage.error("Could not copy response.");
        trackNeoEvent("response copy failed", { message_id: message.id });
      });
  };

  const download = async (format: NeoDocumentFormat) => {
    if (downloading) return;
    setDownloading(true);
    trackNeoEvent("response download started", {
      format,
      message_id: message.id,
      has_content_parts: Boolean(contentParts),
    });
    try {
      await downloadNeoDocument(
        contentParts ? { contentParts } : { markdown: text },
        format
      );
      trackNeoEvent("response download succeeded", {
        format,
        message_id: message.id,
      });
    } catch (error) {
      const detail = error instanceof Error ? error.message : "Unknown error";
      antdMessage.error(`Could not download ${formatLabel(format)}: ${detail}`);
      trackNeoEvent("response download failed", {
        format,
        message_id: message.id,
        error: detail,
      });
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className={styles.messageActions}>
      {text && (
        <button
          type="button"
          onClick={copy}
          aria-label="Copy response"
          title="Copy response"
        >
          <CopyOutlined />
        </button>
      )}
      {isFinal && onRegenerate && (
        <button
          type="button"
          onClick={onRegenerate}
          disabled={streaming}
          aria-label="Regenerate from previous prompt"
          title="Regenerate from previous prompt"
        >
          <ReloadOutlined />
        </button>
      )}
      {showDownload &&
        (hasTabular ? (
          <Dropdown
            menu={{
              items: [
                { key: "pdf", label: "Download as PDF" },
                { key: "xlsx", label: "Download as Excel" },
              ],
              onClick: ({ key }) =>
                void download(key === "xlsx" ? "xlsx" : "pdf"),
            }}
            placement="topRight"
            trigger={["click"]}
          >
            <button
              type="button"
              disabled={downloading}
              aria-label="Download response"
              title="Download response"
            >
              <DownloadOutlined />
            </button>
          </Dropdown>
        ) : json ? (
          <button
            type="button"
            onClick={() => downloadJsonText(json)}
            aria-label="Download response as JSON"
            title="Download response as JSON"
          >
            <DownloadOutlined />
          </button>
        ) : (
          <button
            type="button"
            onClick={() => void download("pdf")}
            disabled={downloading}
            aria-label="Download response as PDF"
            title="Download response as PDF"
          >
            <DownloadOutlined />
          </button>
        ))}
    </div>
  );
};

export default MessageActions;
